import { useState, useEffect, useCallback } from 'react'
import {
  buildPanels,
  type Difficulty,
  type GameMode,
  type MissingItem,
} from './WhatsMissingData'

export type { Difficulty, GameMode }

export interface WhatsMissingState {
  leftSet:      MissingItem[]
  rightSet:     MissingItem[]
  correctIds:   string[]
  selectedIds:  string[]
  wrongFlashId: string | null
  moves:        number
  startTime:    number | null
  endTime:      number | null
  gameComplete: boolean
}

function initState(difficulty: Difficulty, mode: GameMode): WhatsMissingState {
  const { leftSet, rightSet, correctIds } = buildPanels(mode, difficulty)
  return {
    leftSet,
    rightSet,
    correctIds,
    selectedIds:  [],
    wrongFlashId: null,
    moves:        0,
    startTime:    null,
    endTime:      null,
    gameComplete: false,
  }
}

function applyTap(prev: WhatsMissingState, itemId: string): WhatsMissingState {
  if (prev.gameComplete) return prev
  const startTime = prev.startTime ?? Date.now()
  const isCorrect = prev.correctIds.includes(itemId)

  // Toggle selection
  const alreadySelected = prev.selectedIds.includes(itemId)
  const nextSelected = alreadySelected
    ? prev.selectedIds.filter(id => id !== itemId)
    : [...prev.selectedIds, itemId]

  const wrongFlashId = (!isCorrect && !alreadySelected) ? itemId : null

  // Win: selected set equals correct set exactly
  const selectedSet = new Set(nextSelected)
  const correctSet  = new Set(prev.correctIds)
  const gameComplete =
    selectedSet.size === correctSet.size &&
    prev.correctIds.every(id => selectedSet.has(id)) &&
    nextSelected.every(id => correctSet.has(id))

  return {
    ...prev,
    startTime,
    moves:        prev.moves + 1,
    selectedIds:  nextSelected,
    wrongFlashId,
    gameComplete,
    endTime:      gameComplete ? Date.now() : prev.endTime,
  }
}

export function useWhatsMissing(difficulty: Difficulty, mode: GameMode) {
  const [state, setState] = useState<WhatsMissingState>(() => initState(difficulty, mode))

  const resetGame = useCallback(() => {
    setState(initState(difficulty, mode))
  }, [difficulty, mode])

  // Clear wrong flash after 650 ms
  useEffect(() => {
    if (!state.wrongFlashId) return
    const t = setTimeout(() => setState(prev => ({ ...prev, wrongFlashId: null })), 650)
    return () => clearTimeout(t)
  }, [state.wrongFlashId])

  const tapItem = useCallback((itemId: string) => {
    setState(prev => applyTap(prev, itemId))
  }, [])

  return { state, tapItem, resetGame }
}
